import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Pagination,
  Typography,
} from '@mui/material';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { Header } from '../../component/Header';
import { image } from '../../common/Theme';
import Apiconnection from '../../utils/Apiconnection';
import { useNotifications } from '../../context/NotificationContext';

const rowsPerPage = 10;

const formatNotificationTime = (createdAt) => {
  if (!createdAt) {
    return 'Unknown time';
  }

  return dayjs(createdAt).format('DD MMM YYYY, hh:mm A');
};

function NotificationList() {
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingIds, setMarkingIds] = useState([]);
  const { unreadCount, refreshNotifications, markNotificationReadLocally } = useNotifications();

  useEffect(() => {
    const token = localStorage.getItem('prime_access_token');
    if (token) {
      Apiconnection.defaults.headers.token = token;
    }
  }, []);

  useEffect(() => {
    const loadNotifications = async () => {
      setLoading(true);

      try {
        const response = await Apiconnection.post('notification-list', {
          page,
          limit: rowsPerPage,
        });

        if (response?.data?.status) {
          const notificationData = response.data.data || {};
          setNotifications(notificationData.notifications || []);
          setTotalPages(notificationData.totalPages || 1);
        }
      } catch (error) {
        console.error('Error loading notifications:', error);
        toast.error('Unable to load notifications right now.');
      } finally {
        setLoading(false);
      }
    };

    loadNotifications();
  }, [page]);

  const handleMarkAsRead = async (notificationId) => {
    setMarkingIds((currentIds) => [...currentIds, notificationId]);

    try {
      const response = await Apiconnection.post('notification-status-change', {
        notificationId,
        isRead: true,
      });

      if (response?.data?.status) {
        setNotifications((currentNotifications) =>
          currentNotifications.map((item) =>
            item.id === notificationId ? { ...item, isRead: true } : item
          )
        );
        markNotificationReadLocally(notificationId);
        refreshNotifications();
        toast.success('Notification marked as read.');
      }
    } catch (error) {
      console.error('Error updating notification status:', error);
      toast.error('Unable to update notification status.');
    } finally {
      setMarkingIds((currentIds) => currentIds.filter((id) => id !== notificationId));
    }
  };

  return (
    <div
      className="booking-main booking-create-page"
      style={{ backgroundColor: '#f5f7fa', minHeight: '100vh', position: 'relative' }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '350px',
          backgroundColor: '#001b44',
          backgroundImage: `url(${image.bodybg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          zIndex: 0,
        }}
      />
      <div className="booking-inner booking-create-inner" style={{ position: 'relative', zIndex: 1 }}>
        <Header />

        <Container
          maxWidth="lg"
          sx={{
            mt: 2,
            pb: 8,
            fontFamily: "'Inter', 'Poppins', sans-serif",
            backgroundColor: '#fff',
            borderRadius: '24px',
            p: { xs: 3, md: 5 },
            boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', md: 'center' },
              gap: 2,
              mb: 4,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: '60px',
                  height: '60px',
                  backgroundColor: '#f0f7ff',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <NotificationsActiveOutlinedIcon style={{ color: '#004777', fontSize: '28px' }} />
              </Box>
              <Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontFamily: "'Inter', 'Poppins', sans-serif",
                    fontWeight: 800,
                    color: '#1a1a1a',
                    letterSpacing: '-1px',
                    mb: 0.5,
                    fontSize: { xs: '28px', sm: '32px' },
                  }}
                >
                  Notifications
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", color: '#64748b', fontWeight: 500 }}
                >
                  Stay updated with your latest booking and car-ready activity.
                </Typography>
              </Box>
            </Box>

            <Chip
              label={`${unreadCount} unread`}
              sx={{
                backgroundColor: unreadCount ? '#fff4e5' : '#f1f5f9',
                color: unreadCount ? '#b45309' : '#64748b',
                fontWeight: 700,
                fontFamily: "'Inter', 'Poppins', sans-serif",
              }}
            />
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10 }}>
              <CircularProgress sx={{ color: '#004777', mb: 2 }} size={50} thickness={4} />
              <Typography sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", fontWeight: 600 }}>
                Loading Notifications...
              </Typography>
            </Box>
          ) : notifications.length ? (
            <Box sx={{ display: 'grid', gap: 2.5 }}>
              {notifications.map((notification) => {
                const isMarking = markingIds.includes(notification.id);

                return (
                  <Box
                    key={notification.id}
                    sx={{
                      border: notification.isRead ? '1px solid #e2e8f0' : '1px solid #cfe3ff',
                      backgroundColor: notification.isRead ? '#ffffff' : '#f8fbff',
                      borderRadius: '18px',
                      p: { xs: 2.5, md: 3 },
                      boxShadow: notification.isRead ? 'none' : '0 12px 30px rgba(0, 71, 119, 0.08)',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', md: 'center' },
                        gap: 2,
                        mb: 1.5,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                        {!notification.isRead && (
                          <FiberManualRecordIcon sx={{ color: '#0b57d0', fontSize: '0.9rem' }} />
                        )}
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: "'Inter', 'Poppins', sans-serif",
                            fontWeight: 800,
                            color: '#1e293b',
                            fontSize: '1rem',
                          }}
                        >
                          {notification.notiTitle}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={`Booking #${notification.bookingRequestId || 'N/A'}`}
                          sx={{
                            backgroundColor: '#f0f7ff',
                            color: '#004777',
                            fontWeight: 700,
                            fontFamily: "'Inter', 'Poppins', sans-serif",
                          }}
                        />
                        <Chip
                          label={notification.isRead ? 'Read' : 'Unread'}
                          sx={{
                            backgroundColor: notification.isRead ? '#ecfdf3' : '#fff4e5',
                            color: notification.isRead ? '#166534' : '#b45309',
                            fontWeight: 700,
                            fontFamily: "'Inter', 'Poppins', sans-serif",
                          }}
                        />
                      </Box>
                    </Box>

                    <Typography
                      sx={{
                        fontFamily: "'Inter', 'Poppins', sans-serif",
                        color: '#475569',
                        lineHeight: 1.7,
                        mb: 2,
                      }}
                    >
                      {notification.notiBody}
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: 2,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "'Inter', 'Poppins', sans-serif",
                          color: '#64748b',
                          fontWeight: 600,
                          fontSize: '0.9rem',
                        }}
                      >
                        {formatNotificationTime(notification.createdAt)}
                      </Typography>

                      {!notification.isRead && (
                        <Button
                          variant="contained"
                          startIcon={<MarkEmailReadOutlinedIcon />}
                          onClick={() => handleMarkAsRead(notification.id)}
                          disabled={isMarking}
                          sx={{
                            fontFamily: "'Inter', 'Poppins', sans-serif",
                            backgroundColor: '#004777',
                            borderRadius: '100px',
                            px: 3,
                            py: 1.2,
                            fontWeight: 700,
                            textTransform: 'none',
                            '&:hover': { backgroundColor: '#003355' },
                          }}
                        >
                          Mark as Read
                        </Button>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Box
              sx={{
                backgroundColor: '#f8fafc',
                borderRadius: '24px',
                py: 10,
                px: 3,
                textAlign: 'center',
                border: '1px solid #e2e8f0',
              }}
            >
              <Typography
                variant="h4"
                sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", fontWeight: 800, color: '#1e293b', mb: 1.5 }}
              >
                No Notifications Yet
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontFamily: "'Inter', 'Poppins', sans-serif", color: '#64748b', fontWeight: 500 }}
              >
                We will show your latest booking updates here as soon as they arrive.
              </Typography>
            </Box>
          )}

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={5}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(event, value) => setPage(value)}
                color="primary"
                size="large"
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontFamily: "'Inter', 'Poppins', sans-serif",
                    fontWeight: 700,
                    borderRadius: '12px',
                  },
                }}
              />
            </Box>
          )}
        </Container>
      </div>
    </div>
  );
}

export default NotificationList;
